/**
 * List prices, in rupees before 18% GST, for every product on the site.
 *
 * Set on 2026-10-21 from the owner's pricing document (local, not in this
 * repository: Design Documents/Website/PRICING-AND-COSTS.html). This file is
 * public, so it holds the prices and nothing about how they were worked out:
 * no costs, no mark-ups. Change a price there first, then here.
 *
 * The Store's catalogue must carry the same prices before this page goes live,
 * or the page promises one figure and the Store charges another. `npm run
 * check:prices` compares the two and fails on any difference.
 */

export type ProductKey =
  | 'messaging' | 'teams' | 'workbook' | 'hr' | 'files'
  | 'fleet' | 'ca' | 'retail' | 'workspace' | 'detailing';

/** Every page with a product's name on it: the priced products, plus those that come inside one (CRM inside
 *  Messaging) or inside all of them (AI). Only a ProductKey has a price. */
export type PageKey = ProductKey | 'ai' | 'crm' | 'calendar';

export interface Plan {
  name: string;
  /** Users, employees, vehicles or counters included. */
  n: number;
  /** Storage included, GB. */
  gb: number;
  /** A year, before GST. */
  year: number;
  /** The same, a month (billed yearly). */
  month: number;
  /** A year, when the organisation already has another Jackdesk product. */
  added: number;
  /** Free WhatsApp messages included a year, in rupees at Meta's rate. 0 = none. */
  waYear: number;
}

export interface ProductPrice {
  /** What `n` counts. */
  unit: string;
  /** Each one more than the plan includes, a month. */
  extraMonth: number;
  plans: Plan[];
}

export const PRICES: Record<ProductKey, ProductPrice> = {
  messaging: { unit: 'user', extraMonth: 28, plans: [
    { name: 'Solo', n: 2, gb: 10, year: 11999, month: 1000, added: 899, waYear: 567 },
    { name: 'Team', n: 10, gb: 10, year: 14699, month: 1225, added: 3599, waYear: 695 },
    { name: 'Business', n: 25, gb: 10, year: 19699, month: 1642, added: 8599, waYear: 935 },
  ] },
  teams: { unit: 'user', extraMonth: 19, plans: [
    { name: 'Team', n: 10, gb: 5, year: 12899, month: 1075, added: 2299, waYear: 0 },
    { name: 'Business', n: 25, gb: 5, year: 16199, month: 1350, added: 5599, waYear: 0 },
  ] },
  workbook: { unit: 'user', extraMonth: 40, plans: [
    { name: 'Team', n: 10, gb: 5, year: 15499, month: 1292, added: 4899, waYear: 0 },
    { name: 'Business', n: 25, gb: 5, year: 22699, month: 1892, added: 12099, waYear: 0 },
  ] },
  hr: { unit: 'employee', extraMonth: 7, plans: [
    { name: 'Team', n: 25, gb: 10, year: 13399, month: 1117, added: 2799, waYear: 0 },
    { name: 'Business', n: 50, gb: 10, year: 15399, month: 1283, added: 4799, waYear: 0 },
  ] },
  fleet: { unit: 'vehicle', extraMonth: 70, plans: [
    { name: 'Fleet 10', n: 10, gb: 10, year: 8400, month: 700, added: 8400, waYear: 783 },
    { name: 'Fleet 50', n: 50, gb: 10, year: 42000, month: 3500, added: 42000, waYear: 1455 },
  ] },
  ca: { unit: 'staff member', extraMonth: 28, plans: [
    { name: 'Practice', n: 5, gb: 25, year: 13899, month: 1158, added: 2799, waYear: 658 },
    { name: 'Firm', n: 15, gb: 25, year: 17199, month: 1433, added: 6199, waYear: 818 },
  ] },
  workspace: { unit: 'user', extraMonth: 28, plans: [
    { name: 'Team', n: 10, gb: 10, year: 14699, month: 1225, added: 3599, waYear: 695 },
    { name: 'Business', n: 25, gb: 10, year: 19699, month: 1642, added: 8599, waYear: 935 },
  ] },
  detailing: { unit: 'user', extraMonth: 28, plans: [
    { name: 'Office', n: 10, gb: 25, year: 15599, month: 1300, added: 4499, waYear: 738 },
    { name: 'Studio', n: 25, gb: 25, year: 20599, month: 1717, added: 9499, waYear: 978 },
  ] },
  files: { unit: 'user', extraMonth: 27, plans: [
    { name: 'Team', n: 10, gb: 100, year: 15299, month: 1275, added: 4799, waYear: 0 },
    { name: 'Business', n: 25, gb: 500, year: 26099, month: 2175, added: 15599, waYear: 0 },
  ] },
  retail: { unit: 'counter', extraMonth: 28, plans: [
    { name: 'Shop', n: 2, gb: 10, year: 12999, month: 1083, added: 1899, waYear: 615 },
    { name: 'Shop+', n: 5, gb: 10, year: 13999, month: 1167, added: 2899, waYear: 663 },
  ] },
};

/**
 * For "Build your plan": the organisation's own space (its books, data and
 * sign-in) is paid ONCE, however many products it takes; each product adds a
 * fixed part (its included storage) and a part per user, vehicle or counter.
 * The space is a little higher when any product sends WhatsApp, because that
 * share carries the free messages too. Summing these and rounding up to end
 * in 99 gives exactly the plan prices above.
 */
export const SPACE_YEAR = { plain: 10563, withWhatsApp: 11091 };

export const PARTS: Record<ProductKey, { fixedYear: number; perYear: number; whatsapp: boolean }> = {
  messaging: { fixedYear: 157.63, perYear: 336, whatsapp: true },
  teams: { fixedYear: 75.06, perYear: 218.93, whatsapp: false },
  workbook: { fixedYear: 75.06, perYear: 480, whatsapp: false },
  hr: { fixedYear: 790.12, perYear: 80, whatsapp: false },
  fleet: { fixedYear: 1837.63, perYear: 352.8, whatsapp: true },
  ca: { fixedYear: 1066.07, perYear: 336, whatsapp: true },
  workspace: { fixedYear: 157.63, perYear: 336, whatsapp: true },
  detailing: { fixedYear: 1066.07, perYear: 336, whatsapp: true },
  files: { fixedYear: 0, perYear: 320, whatsapp: false },
  retail: { fixedYear: 1165.63, perYear: 336, whatsapp: true },
};

/** Storage above what a plan includes, a GB a month. */
export const STORAGE_GB_MONTH = 1.26;
/** The same, said in words: what 100 GB more costs a month. */
export const STORAGE_100GB = `₹${Math.round(STORAGE_GB_MONTH * 100)}`;
/** Fleet plans include about 22 government lookups a vehicle a year; each beyond that is charged at LOOKUP. */
export const LOOKUPS_INCLUDED = 22;
export const LOOKUP = 8;
/** Rough price of one message read by AI, at Google's own price (nothing added). */
export const AI_PER_MESSAGE = 0.15;

/** Round up to the next figure ending in 99: prices are never rounded down. */
export const to99 = (x: number) => Math.ceil((x + 1) / 100) * 100 - 1;

/** File Storage & Sync sells storage by the GB inside its plans: this much a GB a year. */
export const FILES_GB_YEAR = 15.012;

/** Fleet's flat price a truck a month: everything included, the organisation's space too. */
export const FLEET_TRUCK_MONTH = 70;

export function planTotal(items: { key: ProductKey; n: number; gb?: number }[]): { year: number; month: number } {
  if (!items.length) return { year: 0, month: 0 };
  const fleet = items.filter((i) => i.key === 'fleet');
  const rest = items.filter((i) => i.key !== 'fleet');
  let year = fleet.reduce((s, i) => s + FLEET_TRUCK_MONTH * 12 * i.n, 0);
  if (rest.length) {
    const wa = rest.some((i) => PARTS[i.key].whatsapp);
    // With Fleet in the plan its flat price already carries the organisation's space.
    const space = fleet.length ? 0 : wa ? SPACE_YEAR.withWhatsApp : SPACE_YEAR.plain;
    year += to99(rest.reduce((s, i) => s + PARTS[i.key].fixedYear + PARTS[i.key].perYear * i.n + (i.gb ?? 0) * FILES_GB_YEAR, space));
  }
  return { year, month: Math.round(year / 12) };
}

const inr = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
export const rupees = (v: number) => inr.format(v);
